import { Button } from "@client/components/Button";
import { Modal } from "@client/components/Modal";
import { describeError } from "@client/lib/errorMessage";
import { formatDateTime } from "@client/lib/datetime";
import { describeModelDifferences, type EditorModel } from "@client/lib/scenarioDraft";
import type { ConflictState } from "@client/hooks/useScenarioSave";

/**
 * Two people saved the same scenario, and this is the choice that follows.
 *
 * There are exactly three honest answers and the dialog offers all three. What
 * it never does is pick one: last-write-wins is the default that loses an
 * afternoon of somebody's work without telling either of them, which is why
 * `PUT /model` carries a version at all.
 */
interface Props {
  conflict: ConflictState | null;
  mine: EditorModel;
  busy: boolean;
  error: unknown;
  onKeepMine: () => void;
  onTakeTheirs: () => void;
  onOpenBoth: () => void;
  onCancel: () => void;
}

export function ConflictDialog({
  conflict,
  mine,
  busy,
  error,
  onKeepMine,
  onTakeTheirs,
  onOpenBoth,
  onCancel,
}: Props) {
  if (!conflict) return null;

  const theirs = conflict.current;
  const differences = describeModelDifferences(mine, theirs.model);

  return (
    <Modal
      open
      size="lg"
      title="Someone else saved this scenario first"
      description={`${theirs.author.fullName} saved version ${conflict.currentVersion} at ${formatDateTime(theirs.updatedAt)}, while you were editing. Nothing of yours has been lost — choose what happens to it.`}
      onClose={busy ? () => undefined : onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Keep editing
          </Button>
          <Button variant="ghost" onClick={onOpenBoth} disabled={busy}>
            Open both
          </Button>
          <Button variant="ghost" onClick={onTakeTheirs} disabled={busy}>
            Take theirs
          </Button>
          <Button onClick={onKeepMine} disabled={busy}>
            {busy ? "Saving…" : "Keep mine"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-bold uppercase tracking-wider text-ink-soft">What differs</h3>
        <ul className="flex list-disc flex-col gap-1 pl-5 text-base text-ink">
          {differences.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </div>

      <dl className="flex flex-col gap-1.5 rounded-lg border border-line bg-chip-bg px-3 py-2 text-sm">
        <Choice
          term="Keep mine"
          detail="Saves your copy over theirs as a new version. Their version stays in the history and can be restored."
        />
        <Choice
          term="Take theirs"
          detail="Loads their copy into the editor and discards your unsaved changes."
        />
        <Choice
          term="Open both"
          detail="Copies your work into a brand-new scenario of your own and leaves theirs alone."
        />
      </dl>

      {error !== null && (
        <p role="alert" className="text-sm font-semibold" style={{ color: "var(--chart-crit)" }}>
          {describeError(error)}
        </p>
      )}
    </Modal>
  );
}

function Choice({ term, detail }: { term: string; detail: string }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="font-bold text-ink">{term}</dt>
      <dd className="min-w-[12rem] flex-1 text-ink-soft">{detail}</dd>
    </div>
  );
}
